import {
  type ProviderLiveConnection,
  type ProviderLiveConnectionFailureReason,
} from '@/api/core/provider-live-connection';
import { isApiError } from '@/api/http/errors';
import { InlineLoading } from '@/components/inline-loading';
import { formatDateTimeWithOffset } from '@/lib/format';
import { Button, DetailPage } from '@mgrvtechgroup/admin-ds';
import { useEffect, useRef, useState } from 'react';

type ReadState =
  | { status: 'idle' }
  | { status: 'pending' }
  | { status: 'result'; data: ProviderLiveConnection }
  | { status: 'error'; message: string };

const failureLabels: Record<ProviderLiveConnectionFailureReason, string> = {
  AUTHENTICATION_UNAVAILABLE: '외부 서비스 접속에 필요한 인증 정보를 확인할 수 없습니다.',
  AUTHENTICATION_DENIED: '외부 서비스에서 인증을 거부했습니다.',
  TIMEOUT: '외부 서비스 응답 시간이 초과되었습니다.',
  CONNECTION_FAILED: '외부 서비스에 연결하지 못했습니다.',
  INVALID_RESPONSE: '외부 서비스에서 받은 내용을 확인할 수 없습니다.',
  PROJECT_SCOPE_MISMATCH: '요청한 프로젝트와 받은 내용의 프로젝트가 일치하지 않습니다.',
  REDIRECT_REJECTED: '다른 주소로 이동하려는 시도가 있어 안전을 위해 조회를 중단했습니다.',
  ENVIRONMENT_UNSUPPORTED: '현재 환경의 객실 조회를 지원하지 않습니다.',
  READ_CAPABILITY_UNAVAILABLE: '현재 객실 조회 기능을 사용할 수 없습니다.',
};

function requestError(error: unknown) {
  if (isApiError(error)) {
    if (error.status === 401) return '세션이 만료되었습니다. 다시 로그인해 주세요.';
    if (error.status === 403) return '현재 지점의 조회 권한이 없습니다.';
    if (error.status === 404) return '지점 정보를 찾을 수 없습니다.';
    if (error.status === 409) return '지점 정보가 변경되었습니다. 다시 확인해 주세요.';
    if (error.status === 503) return '현재 조회를 완료하지 못했습니다. 잠시 후 다시 시도해 주세요.';
    if (error.kind === 'invalid-response') return '현재 조회 결과를 확인할 수 없습니다.';
  }
  return '현재 조회를 완료하지 못했습니다. 잠시 후 다시 시도해 주세요.';
}

function resultLabel(data: ProviderLiveConnection) {
  switch (data.status) {
    case 'SUCCEEDED':
      return '이지렘스에서 객실 목록을 정상적으로 불러왔습니다.';
    case 'FAILED':
      return '이지렘스에서 객실 목록을 불러오지 못했습니다.';
    case 'UNKNOWN':
      return '현재 조회 결과를 확인할 수 없습니다.';
    case 'NOT_CONFIGURED':
      return '이지렘스 프로젝트 설정이 없습니다.';
  }
}

export function ProviderLiveConnectionSection({
  onRead,
  isCurrent,
}: {
  onRead: (signal: AbortSignal) => Promise<ProviderLiveConnection>;
  isCurrent: () => boolean;
}) {
  const [state, setState] = useState<ReadState>({ status: 'idle' });
  const requestRef = useRef<AbortController | null>(null);

  useEffect(
    () => () => {
      requestRef.current?.abort();
      requestRef.current = null;
    },
    [],
  );

  const read = async () => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setState({ status: 'pending' });
    try {
      const data = await onRead(controller.signal);
      if (!controller.signal.aborted && isCurrent()) setState({ status: 'result', data });
    } catch (error) {
      if (!controller.signal.aborted && isCurrent())
        setState({ status: 'error', message: requestError(error) });
    } finally {
      if (requestRef.current === controller) requestRef.current = null;
    }
  };

  return (
    <DetailPage.Section
      aria-label="이지렘스 연결 확인"
      title="이지렘스 연결 확인"
      description="이지렘스에서 이 프로젝트의 객실 목록을 불러올 수 있는지 확인합니다. 실제 등록된 객실은 아래 등록 객실 영역에서 따로 조회합니다."
      actions={
        <Button
          size="sm"
          variant="outline"
          onClick={() => void read()}
          loading={state.status === 'pending'}
        >
          연결 확인
        </Button>
      }
    >
      {state.status === 'idle' ? (
        <p role="status" className="body text-muted-foreground">
          연결을 아직 확인하지 않았습니다.
        </p>
      ) : state.status === 'pending' ? (
        <InlineLoading>연결을 확인하고 있습니다.</InlineLoading>
      ) : state.status === 'error' ? (
        <p role="alert" className="body text-warning-foreground">
          {state.message}
        </p>
      ) : (
        <div className="flex min-w-0 flex-col gap-2">
          <p role={state.data.status === 'FAILED' ? 'alert' : 'status'} className="body">
            {resultLabel(state.data)}
          </p>
          {state.data.failure_reason && (
            <p className="body text-muted-foreground">{failureLabels[state.data.failure_reason]}</p>
          )}
          <dl className="grid min-w-0 gap-x-6 gap-y-2 sm:grid-cols-2">
            <div className="min-w-0">
              <dt className="caption text-muted-foreground">조회 범위</dt>
              <dd className="body break-words">프로젝트 객실 목록</dd>
            </div>
            {state.data.observed_at && (
              <div className="min-w-0">
                <dt className="caption text-muted-foreground">확인 시각</dt>
                <dd className="body break-words">
                  {formatDateTimeWithOffset(state.data.observed_at)}
                </dd>
              </div>
            )}
          </dl>
        </div>
      )}
    </DetailPage.Section>
  );
}
